<template>
  <div class="summary-card">
    <div class="card-header">
      <div class="title-section">
        <div class="icon-wrapper">
          <i class="fa-solid fa-rectangle-list"></i>
        </div>
        <h3 class="card-title">{{ title }}</h3>
      </div>

      <div
        class="currency-switcher"
        v-if="showCurrencySwitcher && currency_code.toLowerCase() !== 'eur'"
      >
        <span class="currency-label" :class="{ active: !showEuroInternal }">{{
          currency_code
        }}</span>
        <label class="switch">
          <input
            type="checkbox"
            v-model="showEuroInternal"
            @change="$emit('toggle-currency', showEuroInternal)"
          />
          <span class="slider"></span>
        </label>
        <span class="currency-label" :class="{ active: showEuroInternal }"
          >EUR</span
        >
      </div>
    </div>

    <div class="stats-container" v-if="dataLoaded">
      <div
        class="stat-item"
        v-for="(info, index) in cardsInfo"
        :key="index"
        :class="[info.type + '-card', getNetResultClass(info.type)]"
      >
        <div class="stat-content">
          <div class="stat-icon">
            <i :class="info.icon"></i>
          </div>
          <div class="stat-info">
            <div class="stat-label">{{ info.label }}</div>
            <div class="stat-value">
              {{ formatCurrency(getValue(info.type)) }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="loading-state" v-else>
      <Loader />
    </div>
  </div>
</template>

<script>
import Loader from "./Loader.vue";

export default {
  name: "SummaryCampaign",
  components: { Loader },
  props: {
    title: {
      type: String,
      default: "Summary",
    },
    cardsData: {
      type: Object,
      required: true,
    },
    cardsInfo: {
      type: Array,
      required: true,
    },
    currency_code: {
      type: String,
      default: "EUR",
    },
    showCurrencySwitcher: {
      type: Boolean,
      default: false,
    },
    showEuro: {
      type: Boolean,
      default: false,
    },
    dataLoaded: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      showEuroInternal: this.showEuro,
    };
  },
  watch: {
    showEuro(val) {
      this.showEuroInternal = val;
    },
  },
  methods: {
    getValue(type) {
      const useEuro = this.showEuroInternal;

      if (type === "net") {
        const totalBets = useEuro
          ? this.cardsData.totalBets.converted
          : this.cardsData.totalBets.native;
        const totalWins = useEuro
          ? this.cardsData.totalWins.converted
          : this.cardsData.totalWins.native;
        return totalBets - totalWins;
      }

      if (type === "bets") {
        type = "totalBets";
      }

      if (type === "wins") {
        type = "totalWins";
      }

      const data = this.cardsData[type];
      if (!data) return 0;

      return useEuro ? data.converted : data.native;
    },
    getNetResultClass(type) {
      if (type !== "net") return "";

      const bets = this.getValue("bets");
      const wins = this.getValue("wins");

      if (wins < bets) return "positive";
      if (wins > bets) return "negative";
      return "neutral";
    },
    formatCurrency(value) {
      return `${parseFloat(value).toFixed(2)} ${
        this.showEuroInternal ? "EUR" : this.currency_code
      }`;
    },
  },
};
</script>

<style lang="scss" scoped>
.summary-card {
  padding: 1rem;
  background: linear-gradient(
    135deg,
    rgba(124, 116, 110, 0.1607843137),
    rgba(0, 0, 0, 0.1215686275)
  );
  border: 1px solid #3de380;
  border-radius: 20px;
  color: #ffffff;
  max-width: 100%;
  margin-bottom: 1rem;
  background: rgba(34, 197, 94, 0.1);
  color: #22c55e;
  border: 1px solid rgba(34, 197, 94, 0.2);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid #3a4553;
}

.title-section {
  display: flex;
  align-items: center;
  gap: 12px;
}

.icon-wrapper {
  width: 40px;
  height: 40px;
  background: #16c45b;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
}

.card-title {
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0;
  color: #ffffff;
}

.currency-switcher {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #1e2530;
  padding: 4px;
  border-radius: 20px;
  border: 1px solid #3a4553;
}

.currency-label {
  font-size: 0.75rem;
  font-weight: 500;
  color: #9ca3af;
  padding: 4px 8px;
  border-radius: 16px;
  transition: all 0.2s ease;
  min-width: 40px;
  text-align: center;
}

.currency-label.active {
  color: #6cabff;
  background: rgb(255 255 255 / 10%);
}

.switch {
  position: relative;
  display: inline-block;
  width: 36px;
  height: 20px;
}

.switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #3a4553;
  transition: 0.3s;
  border-radius: 10px;
}

.slider:before {
  position: absolute;
  content: "";
  height: 14px;
  width: 14px;
  left: 3px;
  bottom: 3px;
  background-color: #9ca3af;
  transition: 0.3s;
  border-radius: 50%;
}

input:checked + .slider {
  background-color: #6cabff;
}

input:checked + .slider:before {
  background-color: white;
  transform: translateX(16px);
}

.stats-container {
  display: flex;
  flex-direction: row;
  gap: 0.55rem;

  @media (max-width: 991px) {
    flex-direction: column;
  }
}

.stat-item {
  background: #1e25304a;
  border-radius: 12px;
  padding: 20px;
  border: 1px solid #bdcbdc45;
  flex: 1 1 auto;
  transition: all 0.2s ease;
}

.stat-content {
  display: flex;
  align-items: center;
  gap: 16px;
}

.stat-icon {
  width: 48px;
  height: 48px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: linear-gradient(135deg, #3b82f6, #8b5cf6) !important;
  color: #fff !important;
}

.stat-info {
  flex: 1;
}

.stat-label {
  font-size: 0.875rem;
  color: #9ca3af;
  font-weight: 500;
  margin-bottom: 4px;
}

.stat-value {
  font-size: 1.5rem;
  font-weight: 700;
  color: #ffffff;
}

.net-card.positive .net-icon {
  background: rgba(34, 197, 94, 0.1);
  color: #22c55e;
}

.net-card.positive .stat-value {
  color: #22c55e;
}

.net-card.negative .net-icon {
  background: rgba(239, 68, 68, 0.1);
  color: #ef4444;
}

.net-card.negative .stat-value {
  color: #ef4444;
}

.net-card.neutral .net-icon {
  background: rgba(245, 158, 11, 0.1);
  color: #f59e0b;
}

.net-card.neutral .stat-value {
  color: #ffffff;
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 40px 20px;
}

.spinner {
  width: 32px;
  height: 32px;
  border: 3px solid #3a4553;
  border-top: 3px solid #4f46e5;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.loading-state p {
  margin: 0;
  color: #9ca3af;
  font-size: 0.875rem;
}
</style>
